// react
import React, { Suspense, lazy } from 'react';

// react router
import { Route, Switch } from 'react-router';
import { Redirect } from 'react-router-dom';

import Loading from '../../component/Common/Misc/Loading';
import PageLoadError from '../../component/App/PageLoadError';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Example "componentStack":
    //   in ComponentThatThrows (created by App)
    //   in ErrorBoundary (created by App)
    //   in div (created by App)
    //   in App
    // logErrorToMyService(error, info.componentStack);
    console.log(error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return this.props.fallback;
    }

    return this.props.children;
  }
}

const Loadable = ({ loader, loading }) => {
  const LazyComponent = lazy(loader);
  return (props) => (
    <ErrorBoundary fallback={<PageLoadError />}>
      <Suspense fallback={loading}>
        <LazyComponent {...props} />
      </Suspense>
    </ErrorBoundary>
  );
}

// Loading component for dynamic imports
const LoadingComponent = ({ isLoading, error }) => {
  // Handle the loading state
  if (isLoading) {
    return <Loading />;
  }
  // Handle the error state
  else if (error) {
    console.log(error)
    return <PageLoadError />;
  }
  else {
    return null;
  }
};

const GymWrapper = Loadable({
  loader: () => import('../Auth/GymWrapper'),
  loading: LoadingComponent,
});
const Login = Loadable({
  loader: () => import('../Auth/Login'),
  loading: LoadingComponent,
});
const Register = Loadable({
  loader: () => import('../Auth/Register'),
  loading: LoadingComponent,
});
const ResetPassword = Loadable({
  loader: () => import('../Auth/ResetPassword'),
  loading: LoadingComponent,
});
const GymView = Loadable({
  loader: () => import('../Gym/GymView'),
  loading: LoadingComponent,
});
const NewGymView = Loadable({
  loader: () => import('../Gym/NewGymView'),
  loading: LoadingComponent,
});
const GymMapView = Loadable({
  loader: () => import('../GymMap/GymMapView2'),
  loading: LoadingComponent,
});
const GymMapEngagementView = Loadable({
  loader: () => import('../GymMap/GymMapView'),
  loading: LoadingComponent,
});
const GymMapEditView = Loadable({
  loader: () => import('../GymMap/GymMapEditView'),
  loading: LoadingComponent,
});
const GymMapEmbedView = Loadable({
  loader: () => import('../GymMap/GymMapEmbedView'),
  loading: LoadingComponent,
});
const GymSettingsView = Loadable({
  loader: () => import('../Settings/GymSettingsView'),
  loading: LoadingComponent,
});
const CircuitSettingsView = Loadable({
  loader: () => import('../Settings/CircuitSettingsView'),
  loading: LoadingComponent,
});
const CircuitCustomSettingsView = Loadable({
  loader: () => import('../Settings/CircuitCustomSettingsView'),
  loading: LoadingComponent,
});
const GradeTargetSettingsView = Loadable({
  loader: () => import('../Settings/GradeTargetSettingsView'),
  loading: LoadingComponent,
});
const UserSettingsView = Loadable({
  loader: () => import('../Settings/UserSettingsView'),
  loading: LoadingComponent,
});
const UserDestinationSettingsView = Loadable({
  loader: () => import('../Settings/UserDestinationSettingsView'),
  loading: LoadingComponent,
});
const LocationPayeeSettingsView = Loadable({
  loader: () => import('../Settings/LocationPayeeSettingsView'),
  loading: LoadingComponent,
});
const BillingInfoView = Loadable({
  loader: () => import('../Settings/BillingInfoView'),
  loading: LoadingComponent,
});
const SettersView = Loadable({
  loader: () => import('../Setters/SettersView'),
  loading: LoadingComponent,
});
const ProfileView = Loadable({
  loader: () => import('../Profile/ProfileView'),
  loading: LoadingComponent,
});
const ProfileSettingsView = Loadable({
  loader: () => import('../Profile/ProfileSettingsView'),
  loading: LoadingComponent,
});
const PlacardSettingsView = Loadable({
  loader: () => import('../Settings/PlacardSettingsView'),
  loading: LoadingComponent,
});
const RedpointSettingsView = Loadable({
  loader: () => import('../Settings/RedpointSettingsView'),
  loading: LoadingComponent,
});
const TagSettingsView = Loadable({
  loader: () => import('../Settings/TagSettings'),
  loading: LoadingComponent,
});
const ContactsSettingsView = Loadable({
  loader: () => import('../Settings/ContactsSettingsView'),
  loading: LoadingComponent,
});
const WallView = Loadable({
  loader: () => import('../Wall/WallView'),
  loading: LoadingComponent,
});
const MultiWallView = Loadable({
  loader: () => import('../Wall/MultiWallView'),
  loading: LoadingComponent,
});
const WallSettingsView = Loadable({
  loader: () => import('../Wall/WallSettingsView'),
  loading: LoadingComponent,
});
const WallResetView = Loadable({
  loader: () => import('../Wall/WallResetView'),
  loading: LoadingComponent,
});
const ReportView = Loadable({
  loader: () => import('../Report/ReportView'),
  loading: LoadingComponent,
});
const FullReportView = Loadable({
  loader: () => import('../Report/FullReportView'),
  loading: LoadingComponent,
});
const AutomatedReportsView = Loadable({
  loader: () => import('../Report/AutomatedReportsView'),
  loading: LoadingComponent,
});
const GymStatusView = Loadable({
  loader: () => import('../Admin/GymStatusView'),
  loading: LoadingComponent,
});
const ChallengesView = Loadable({
  loader: () => import('../Challenge/ChallengesView'),
  loading: LoadingComponent,
});
const ChallengeDetailsView = Loadable({
  loader: () => import('../Challenge/ChallengeDetailsView'),
  loading: LoadingComponent,
});
const CreateEditChallengeView = Loadable({
  loader: () => import('../Challenge/CreateEditChallengeView'),
  loading: LoadingComponent,
});
const GuidebookView = Loadable({
  loader: () => import('../Destination/GuidebookView'),
  loading: LoadingComponent,
})
const GymNewsPostsView = Loadable({
  loader: () => import('../GymNewsPost/GymNewsPostsView'),
  loading: LoadingComponent,
});

export default function Routes(props) {
  if (props.isLoggedIn && props.user) {
    return (
      <ErrorBoundary fallback={<PageLoadError />}>
        <Switch>
          <Route
            exact
            path="/"
            render={() => (
              <GymWrapper {...props}>
                <GymView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/login"
            render={() => <Redirect to="/" />}
          />
          <Route
            exact
            path="/home"
            render={() => (
              <GymWrapper {...props}>
                <GymView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            exact
            path="/new-gym"
            render={() => (
              <GymWrapper {...props} gymsNotRequired>
                <NewGymView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/destination/:destination_id/profile/:user_slug/settings"
            render={() => (
              <GymWrapper {...props} gymsNotRequired>
                <ProfileSettingsView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/destination/:destination_id/settings/users"
            render={() => (
              <GymWrapper {...props}>
                <UserDestinationSettingsView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/destination/:destination_id/settings/payees"
            render={() => (
              <GymWrapper {...props}>
                <LocationPayeeSettingsView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/destination/:destination_id/:tab_name?/:climb_type_id?"
            render={() => (
              <GymWrapper {...props} gymsNotRequired>
                <GuidebookView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/gym/:gym_slug/wall/:wall_slug/settings"
            render={() => (
              <GymWrapper {...props}>
                <WallSettingsView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/gym/:gym_slug/wall/:wall_slug/reset/:from_map?"
            render={() => (
              <GymWrapper {...props}>
                <WallResetView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/gym/:gym_slug/wall/:wall_slug/set/:wall_set_id/climb/:climb_id"
            render={() => (
              <GymWrapper {...props}>
                <WallView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/gym/:gym_slug/wall/:wall_slug/set/:wall_set_id"
            render={() => (
              <GymWrapper {...props}>
                <WallView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/gym/:gym_slug/wall/:wall_slug"
            render={() => (
              <GymWrapper {...props}>
                <WallView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/assignment/:gym_slug"
            render={() => (
              <GymWrapper {...props}>
                <MultiWallView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/map/:gym_slug/edit"
            render={() => (
              <GymWrapper {...props}>
                <GymMapEditView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/map/:gym_slug"
            render={() => (
              <GymWrapper {...props}>
                <GymMapView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/challenges/create/:gym_slug/:template_id/:duplicate_challenge_id"
            render={() => (
              <GymWrapper {...props}>
                <CreateEditChallengeView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/challenges/create/:gym_slug/:template_id"
            render={() => (
              <GymWrapper {...props}>
                <CreateEditChallengeView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/challenges/edit/:challenge_id"
            render={() => (
              <GymWrapper {...props}>
                <CreateEditChallengeView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/challenges/view/:challenge_id"
            render={() => (
              <GymWrapper {...props}>
                <ChallengeDetailsView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/challenges/:gym_slug"
            render={() => (
              <GymWrapper {...props}>
                <ChallengesView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/news/:gym_slug"
            render={() => (
              <GymWrapper {...props}>
                <GymNewsPostsView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/settings/gym/:gym_slug"
            render={() => (
              <GymWrapper {...props}>
                <GymSettingsView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/settings/colors/:gym_slug"
            render={() => (
              <GymWrapper {...props}>
                <CircuitSettingsView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/settings/circuits/:gym_slug"
            render={() => (
              <GymWrapper {...props}>
                <CircuitCustomSettingsView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/settings/tags/:gym_slug?"
            render={() => (
              <GymWrapper {...props}>
                <TagSettingsView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/settings/contacts/:gym_slug?"
            render={() => (
              <GymWrapper {...props}>
                <ContactsSettingsView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/settings/targets/:gym_slug?"
            render={() => (
              <GymWrapper {...props}>
                <GradeTargetSettingsView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/settings/users/:gym_slug"
            render={() => (
              <GymWrapper {...props}>
                <UserSettingsView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/settings/placard/:gym_slug"
            render={() => (
              <GymWrapper {...props}>
                <PlacardSettingsView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/settings/kaya/:gym_slug"
            render={() => (
              <GymWrapper {...props}>
                <RedpointSettingsView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/profile/:user_slug/settings"
            render={() => (
              <GymWrapper {...props} gymsNotRequired>
                <ProfileSettingsView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/profile/:user_slug"
            render={() => (
              <GymWrapper {...props} gymsNotRequired>
                <ProfileView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/setters/:gym_slug"
            render={() => (
              <GymWrapper {...props} gymsNotRequired>
                <SettersView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/settings/billing"
            render={() => (
              <GymWrapper {...props} gymsNotRequired>
                <BillingInfoView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/gym/:gym_slug/:climb_type_id?"
            render={() => (
              <GymWrapper {...props}>
                <GymView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/reports/full"
            render={() => (
              <GymWrapper {...props}>
                <FullReportView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/reports/basic"
            render={() => (
              <GymWrapper {...props}>
                <ReportView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/reports/automated"
            render={() => (
              <GymWrapper {...props}>
                <AutomatedReportsView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/23b8182b-8f33-4a07-a91c-8bc4d29d16cb"
            render={() => (
              <GymWrapper {...props}>
                <GymStatusView {...props} />
              </GymWrapper>
            )}
          />
          <Route
            path="/embed/map/:id/:referrer"
            render={() => (
              <GymMapEmbedView {...props} />
            )}
          />
          <Route
            path="/"
            render={() => (
              <Redirect to="/" />
            )}
          />
        </Switch>
      </ErrorBoundary>
    );
  }
  return (
    <Switch>
      <Route
        path="/login"
        render={() => <Login />}
      />
      <Route
        path="/register/:email?"
        render={() => <Register />}
      />
      <Route
        path="/reset-password/:reset_pw_link?"
        render={() => <ResetPassword />}
      />
      <Route
        path="/embed/map/:id/:referrer"
        render={() => (
          <GymMapEmbedView {...props} />
        )}
      />
      <Route
        path="/"
        render={() => <Login />}
      />
    </Switch>
  );
}
